import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";

export type ListProps<T> = {
  items: T[];
  keyOf: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Chat mode: starts at the newest (last) item, sticks to the bottom, `onEndReached` = reached the oldest end. */
  inverted?: boolean;
  onEndReached?: () => void;
  header?: ReactNode;
  empty?: ReactNode;
  /** Floating content over the top of the list (e.g. the "N new messages" pill). */
  overlay?: ReactNode;
  /** Item to bring into view on first render (e.g. the "new" divider) instead of the default end. */
  initialKey?: string;
  /** Scrolls to `key` whenever `seq` changes; an empty key scrolls to the end. */
  scrollTo?: { key: string; seq: number };
  /** Keys of the first and last rendered-visible items (e.g. to know whether a marker is above the viewport). */
  onVisibleRangeChange?: (firstKey: string | undefined, lastKey: string | undefined) => void;
  testID?: string;
};

const START_INDEX = 1_000_000;

/**
 * How many items were added before the items that were already there (an older page), so `firstItemIndex` can drop
 * by exactly that much and Virtuoso keeps the scroll position. The shift every kept item agrees on most wins, rather
 * than where the old first item went: a chat list's first item is often a divider that moves (the day divider of a
 * day whose earlier messages just loaded goes above them), and following it under-counts the prepend (the content
 * jumps by the rows it missed). Appends, edits and removals further down don't shift anything.
 */
export function prependedCount(before: readonly string[], after: readonly string[]): number {
  if (before.length === 0) return 0;
  const at = new Map<string, number>();
  after.forEach((key, i) => at.set(key, i));
  const votes = new Map<number, number>();
  let best = 0;
  let bestVotes = 0;
  before.forEach((key, i) => {
    const j = at.get(key);
    if (j === undefined) return;
    const shift = j - i;
    const n = (votes.get(shift) ?? 0) + 1;
    votes.set(shift, n);
    if (n > bestVotes) {
      best = shift;
      bestVotes = n;
    }
  });
  return Math.max(0, best);
}

type ListContext = { header: ReactNode };

function ListHeader({ context }: { context: ListContext }) {
  return <>{context.header}</>;
}

const COMPONENTS = { Header: ListHeader };

/** Virtualized list (react-virtuoso). Keeps the scroll position when older items are prepended. */
export function List<T>(p: ListProps<T>) {
  const ref = useRef<VirtuosoHandle>(null);
  const keys = useMemo(() => p.items.map(p.keyOf), [p.items, p.keyOf]);
  // Detect prepends (older pages) and shift firstItemIndex by the number of new leading items
  // ("adjusting state while rendering", so Virtuoso keeps the scroll position).
  const [tracked, setTracked] = useState({ keys, firstIndex: START_INDEX });
  let firstIndex = tracked.firstIndex;
  if (tracked.keys !== keys) {
    firstIndex = tracked.firstIndex - prependedCount(tracked.keys, keys);
    setTracked({ keys, firstIndex });
  }
  const end = useRef(p.onEndReached);
  useLayoutEffect(() => {
    end.current = p.onEndReached;
  });

  const seq = p.scrollTo?.seq;
  useEffect(() => {
    if (p.scrollTo === undefined) return;
    if (p.scrollTo.key === "") {
      // After the parent appends (my own message) or a row grows (an ephemeral answer under it), wait two frames: one
      // for the new content, one for its measured height.
      let id = requestAnimationFrame(() => {
        id = requestAnimationFrame(() => ref.current?.scrollToIndex({ index: "LAST", align: "end", behavior: "smooth" }));
      });
      return () => cancelAnimationFrame(id);
    }
    const i = keys.indexOf(p.scrollTo.key);
    if (i >= 0) ref.current?.scrollToIndex({ index: i, align: "start", behavior: "smooth" });
    // Only a new request (seq) scrolls; item changes alone must not.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [seq]);

  // Chat lists open at the newest item even when row heights are only known after the first paint.
  const hasItems = p.items.length > 0;
  const startAtEnd = p.inverted === true && p.initialKey === undefined;
  useEffect(() => {
    if (!hasItems || !startAtEnd) return;
    const id = requestAnimationFrame(() => ref.current?.scrollToIndex({ index: "LAST", align: "end" }));
    return () => cancelAnimationFrame(id);
  }, [hasItems, startAtEnd]);

  if (p.items.length === 0) {
    return (
      <div className="b-list b-list-empty" data-testid={p.testID}>
        {p.header}
        {p.empty}
        {p.overlay === undefined ? null : <div className="b-list-overlay">{p.overlay}</div>}
      </div>
    );
  }

  const initial = p.initialKey === undefined ? -1 : keys.indexOf(p.initialKey);
  const inverted = p.inverted === true;
  return (
    <div className="b-list" data-testid={p.testID}>
      <Virtuoso<T, ListContext>
        ref={ref}
        className="b-list-scroller"
        data={p.items}
        firstItemIndex={firstIndex}
        computeItemKey={(_i, item) => p.keyOf(item)}
        itemContent={(_i, item) => p.renderItem(item)}
        initialTopMostItemIndex={initial >= 0 ? { index: initial, align: "start" } : inverted ? { index: p.items.length - 1, align: "end" } : 0}
        alignToBottom={inverted}
        followOutput={inverted ? (atBottom) => (atBottom ? "smooth" : false) : undefined}
        atBottomThreshold={120}
        startReached={inverted ? () => end.current?.() : undefined}
        endReached={inverted ? undefined : () => end.current?.()}
        increaseViewportBy={{ top: 400, bottom: 400 }}
        // Measure rows in the ResizeObserver callback itself (before paint) instead of a frame later: rows rendered
        // above while scrolling up get their real height and the upward-scroll compensation in the same frame, so the
        // content doesn't jump down and back (scroll-up jitter). Costs only the benign "ResizeObserver loop" notice.
        skipAnimationFrameInResizeObserver
        rangeChanged={
          p.onVisibleRangeChange === undefined
            ? undefined
            : (range) => p.onVisibleRangeChange?.(keys[range.startIndex - firstIndex], keys[range.endIndex - firstIndex])
        }
        context={{ header: p.header }}
        components={COMPONENTS}
      />
      {p.overlay === undefined ? null : <div className="b-list-overlay">{p.overlay}</div>}
    </div>
  );
}
